import BaseAclContainer from '@/account/component/AclContainer';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { registration } from '@/api';
import type { ExportEmailAsCSVRquest } from '@/api/registration';
import { PermissionAction, PermissionDomain } from '@/api/constant/adminUserPermission';
import { type DatatableSelectOptions } from '@/api/tenantClient';
import { generatePageUrl } from '@/app/pages';
import { useAdminAuth } from '@/app/ProviderAdminAuth';
import { UiBadge, UiButton, UiHStack, UiIconArrowRight, uiStyles } from '@/lib/ui';
import { SavedViewsDropdown } from '@/lib/ui/SavedViews';
import { type UseSavedViewsReturn } from '@/lib/hook/useSavedViews';
import { useDataTableOptions } from '@/registration/hook/useDataTableOptions';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useMutation } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
  MantineReactTable,
  type MRT_VisibilityState,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState
} from 'mantine-react-table';
import type React from 'react';
import { useCallback, useMemo, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { type SerializedOrderTableRow } from './OrderTable';
import { useGlobalToast } from '@/lib/hook';

dayjs.extend(advancedFormat);
dayjs.extend(timezone);

interface DatatableProps {
  data: SerializedOrderTableRow[]
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
  filters: MRT_ColumnFiltersState
  setFilters: React.Dispatch<React.SetStateAction<MRT_ColumnFiltersState>>
  search?: string
  setSearch: (searchText: string) => void
  sorting: MRT_SortingState
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>
  pagination: MRT_PaginationState
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>
  columnVisibility: MRT_VisibilityState
  setColumnVisibility: React.Dispatch<React.SetStateAction<MRT_VisibilityState>>
  rowCount: number
  paymentOptions: DatatableSelectOptions
  eventSelectOptions: DatatableSelectOptions
  delegateTypeSelectOptions: DatatableSelectOptions
  statusSelectOptions: DatatableSelectOptions
  workshopTicketsSelectOptions: DatatableSelectOptions
  functionTicketsSelectOptions: DatatableSelectOptions
  tourTicketsSelectOptions: DatatableSelectOptions
  savedViews: UseSavedViewsReturn
}

const Datatable: FC<DatatableProps> = ({
  data, isLoading, isError, isFetching, refetch, filters, setFilters, sorting, setSorting, search, setSearch,
  pagination, setPagination, columnVisibility, setColumnVisibility, rowCount, eventSelectOptions, statusSelectOptions, delegateTypeSelectOptions, workshopTicketsSelectOptions, functionTicketsSelectOptions, paymentOptions, tourTicketsSelectOptions, savedViews
}) => {
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { tenantCode, tenant } = useRegisterRoute();
  const { createTenantAdminApiRequest } = useTenantApi();
  const { adminAuth } = useAdminAuth();
  const { reportToGlobal } = useApiErrorHandler();
  const dataTableOptions = useDataTableOptions<SerializedOrderTableRow>();

  const buildOrderCsvPayload = useCallback((): ExportEmailAsCSVRquest => {
    const payload: ExportEmailAsCSVRquest = {
      model: 'Order',
      email: adminAuth.user?.email ?? '',
      filters: JSON.stringify(filters ?? []),
      sorting: JSON.stringify(sorting ?? []),
      column_visibility: JSON.stringify(columnVisibility ?? {}),
      size: rowCount,
    };

    if (search) {
      payload.search = search;
    }

    return payload;
  }, [adminAuth.user?.email, filters, sorting, columnVisibility, rowCount, search]);

  const { mutate: exportEmailAsCSV, isLoading: isExportCSVLoading } = useMutation<{}, Error, ExportEmailAsCSVRquest>({
    mutationFn: async (payload) => {
      return await registration.exportEmailAsCSV(createTenantAdminApiRequest)(payload);
    },
    onError: (error) => {
      reportToGlobal(error);
    }
  });

  const { mutate: exportOrderFinanceAsCSV, isLoading: isExportOrderFinanceLoading } = useMutation<{}, Error>({
    mutationFn: async () => {
      return await registration.exportEmailAsCSV(createTenantAdminApiRequest)({
        model: 'Order-Finance',
        email: adminAuth.user?.email ?? ''
      });
    },
    onError: (error) => {
      reportToGlobal(error);
    }
  });

  const onExportEmailAsCSV = useCallback(() => {
    showToast.success('CSV email sent!');
    return exportEmailAsCSV(buildOrderCsvPayload());
  }, [exportEmailAsCSV, buildOrderCsvPayload, showToast]);

  const onExportOrderFinanceAsCSV = useCallback(() => { return exportOrderFinanceAsCSV(); }, [exportOrderFinanceAsCSV]);
  const isShowExportOrderFinanceButton = useMemo(() => { return localStorage.getItem('SHOW_FINANCE_CSV_BUTTON') === 'true'; }, []);

  const toOrderInfoPage = useCallback(
    (orderId: number) => {
      navigate(generatePageUrl('RegistrationManageOrderInfo', { tenantCode, orderId: `${orderId}` }));
    },
    [tenantCode]
  );

  const columns = useMemo<Array<MRT_ColumnDef<SerializedOrderTableRow>>>(
    () => {
      return [
        {
          accessorKey: 'event.id',
          header: 'Event',
          filterVariant: 'select',
          mantineFilterSelectProps: {
            data: eventSelectOptions
          },
          Cell: ({ row }) => {
            return row.original.event.name;
          }
        },
        {
          accessorKey: 'status',
          header: 'Status',
          filterVariant: 'select',
          mantineFilterSelectProps: {
            data: statusSelectOptions
          }
        },
        {
          accessorKey: 'invoiceNumber',
          header: 'Invoice number'
        },
        {
          minSize: 350,
          accessorKey: 'paymentTransactionId',
          header: 'Payment transactions',
          enableSorting: false
        },
        {
          minSize: 350,
          accessorKey: 'paymentTransactionSuccessful',
          header: 'Successful transaction id',
          enableSorting: false
        },
        {
          accessorKey: 'delegateType',
          header: 'Registration type',
          filterVariant: 'multi-select',
          mantineFilterSelectProps: {
            data: delegateTypeSelectOptions
          },
          size: 300,
          enableSorting: false,
          Cell: ({ cell }) => {
            const tickets = cell.getValue() as Array<{ name: string, quantity: number, type: string }>;
            return (
              <>
                {tickets.map((ticket, index) => {
                  return (
                    <UiBadge key={index} colorScheme="blue" mr={1}>
                      {`${ticket.name}`}
                    </UiBadge>
                  );
                })}
              </>
            );
          }
        },
        {
          accessorKey: 'ticket.function',
          header: 'Function tickets',
          filterVariant: 'multi-select',
          mantineFilterSelectProps: {
            data: functionTicketsSelectOptions
          },
          size: 300,
          enableSorting: false,
          Cell: ({ cell }) => {
            const tickets = cell.getValue() as Array<{ name: string, quantity: number, type: string }>;
            return (
              <>
                {tickets.map((ticket, index) => {
                  return (
                    <UiBadge key={index} colorScheme="blue" mr={1}>
                      {`${ticket.name}`}
                    </UiBadge>
                  );
                })}
              </>
            );
          }
        },
        {
          accessorKey: 'ticket.workshop',
          header: 'Workshop tickets',
          filterVariant: 'multi-select',
          enableSorting: false,
          size: 300,
          mantineFilterSelectProps: {
            data: workshopTicketsSelectOptions
          },
          Cell: ({ cell }) => {
            const tickets = cell.getValue() as Array<{ name: string, quantity: number, type: string }>;
            return (
              <>
                {tickets.map((ticket, index) => {
                  return (
                    <UiBadge key={index} colorScheme="blue" mr={1}>
                      {`${ticket.name}`}
                    </UiBadge>
                  );
                })}
              </>
            );
          }
        },
        {
          accessorKey: 'ticket.tour',
          header: 'Tour tickets',
          filterVariant: 'multi-select',
          enableSorting: false,
          size: 300,
          mantineFilterSelectProps: {
            data: tourTicketsSelectOptions
          },
          Cell: ({ cell }) => {
            const tickets = cell.getValue() as Array<{ name: string, quantity: number, type: string }>;
            return (
              <>
                {tickets.map((ticket, index) => {
                  return (
                    <UiBadge key={index} colorScheme="blue" mr={1}>
                      {`${ticket.name}`}
                    </UiBadge>
                  );
                })}
              </>
            );
          }
        },
        {
          accessorKey: 'attendee.name',
          header: 'Name'
        },
        {
          accessorKey: 'attendee.email',
          header: 'Email'
        },
        {
          accessorKey: 'userId',
          header: 'User ID'
        },
        {
          accessorKey: 'userCompany',
          header: 'User company'
        },
        {
          accessorKey: 'attendee.memberId',
          header: 'Member ID'
        },
        {
          accessorKey: 'attendeeCategory.name',
          header: 'Attendee group'
        },
        {
          accessorKey: 'discountCode',
          header: 'Discount code'
        },
        {
          accessorKey: 'country',
          header: 'Country'
        },
        {
          accessorKey: 'state',
          header: 'State'
        },
        {
          accessorKey: 'totalPriceFormat',
          header: 'Total price',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'refundAmount',
          header: 'Refund Amount',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'surcharge',
          header: 'Surcharge',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'surchargeFormat',
          header: 'Surcharge (Formatted)',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'refundedAt',
          header: 'Refunded At',
          enableColumnFilter: false,
          Cell: ({ cell, row }) => {
            return cell.getValue()
              ? dayjs.tz(cell.getValue() as string | number | Date | Dayjs, row.original.event.timeZoneName).format('Do MMMM YYYY, hh:mm A Z')
              : '';
          }
        },
        {
          accessorKey: 'refundedByName',
          header: 'Refunded By',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'financeCode',
          header: 'Finance Code',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'financeCodeTwo',
          header: 'Finance Code 2',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'taxName',
          header: 'Tax Name',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'taxFormat',
          header: 'Tax (Formatted)',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string;
          }
        },
        {
          accessorKey: 'adminsNotes',
          header: "Admin's Notes",
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            const value = cell.getValue();
            if (Array.isArray(value)) {
              return value.join(', ');
            }
            return value ? String(value) : '';
          }
        },
        {
          accessorKey: 'purchasedAt',
          header: 'Purchased at date',
          id: 'purchasedAtDate',
          Cell: ({ cell, row }) => {
            return cell.getValue() ? dayjs.tz(cell.getValue() as string | number | Date | Dayjs, row.original.event.timeZoneName).format('Do MMMM') : null;
          },
          filterVariant: 'date-range'
        },
        {
          accessorKey: 'purchasedAt',
          header: 'Purchased at time',
          id: 'purchasedAtTime',
          Cell: ({ cell, row }) => {
            return cell.getValue() ? dayjs.tz(cell.getValue() as string | number | Date | Dayjs, row.original.event.timeZoneName).format('hh:mm A Z') : null;
          },
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          accessorKey: 'paymentOption',
          header: 'Payment option',
          filterVariant: 'select',
          mantineFilterSelectProps: {
            data: paymentOptions
          }
        },
        {
          accessorKey: 'offlinePaymentType',
          header: 'Offline Payment Type',
          enableColumnFilter: false,
          Cell: ({ cell }) => {
            return cell.getValue() as string || '';
          }
        },
        {
          accessorKey: 'createdAt',
          header: 'Created at date',
          id: 'createdAtDate',
          Cell: ({ cell, row }) => {
            return cell.getValue() ? dayjs.tz(cell.getValue() as string | number | Date | Dayjs, row.original.event.timeZoneName).format('Do MMMM') : '';
          },
          filterVariant: 'date-range'
        },
        {
          accessorKey: 'createdAt',
          header: 'Created at time',
          id: 'createdAtTime',
          Cell: ({ cell, row }) => {
            return cell.getValue() ? dayjs.tz(cell.getValue() as string | number | Date | Dayjs, row.original.event.timeZoneName).format('hh:mm A Z') : '';
          },
          enableColumnFilter: false,
          enableSorting: false,
        },

      ];
    },
    [delegateTypeSelectOptions, eventSelectOptions, functionTicketsSelectOptions, statusSelectOptions, tourTicketsSelectOptions, workshopTicketsSelectOptions, paymentOptions]
  );

  const handleApplyFilter = () => {
    refetch();
  };

  const handleClearFilters = () => {
    setFilters([]);
    setPagination({ pageIndex: 0, pageSize: 10 });
    setSearch('');
    setSorting([]);
  };

  const table = useMantineReactTable({
    columns,
    data,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: !!search,
      columnVisibility,
      columnPinning: {
        right: ['mrt-row-actions']
      }
    },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnPinning: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    rowCount,
    state: {
      columnFilters: filters.map((filter) => {
        if (['createdAtDate', 'purchasedAtDate'].includes(filter.id)) {
          return {
            ...filter,
            value: (filter.value as Array<string | number | Date | dayjs.Dayjs | null | undefined>).map((date) => {
              if (!date) return '';
              return new Date(date as string);
            })
          };
        }
        return filter;
      }),
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      sorting,
      pagination,
      globalFilter: search,
      columnVisibility
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 96
      }
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    globalFilterFn: 'contains',
    renderTopToolbarCustomActions: () => {
      return (
        <UiHStack
          spacing={8}
          flexGrow={1}
          justifyContent={'flex-end'}
          px={8}
        >
          <SavedViewsDropdown savedViews={savedViews} />
          {isShowExportOrderFinanceButton && (
            <UiButton
              px={0}
              size={'sm'}
              variant={'ghost'}
              colorScheme={'primary'}
              onClick={onExportOrderFinanceAsCSV}
              isLoading={isExportOrderFinanceLoading}>
              Email finance report as CSV
            </UiButton>
          )}
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={onExportEmailAsCSV}
            isLoading={isExportCSVLoading}>
            Email as CSV
          </UiButton>
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={handleClearFilters}>
            Clear all filters
          </UiButton>
          <UiButton
            px={0}
            size={'sm'}
            variant={'ghost'}
            colorScheme={'primary'}
            onClick={handleApplyFilter}>
            Apply filter
          </UiButton>
        </UiHStack>
      );
    },
    renderRowActions: ({ row }) => {
      return (
        <BaseAclContainer tenantId={tenant?.id ?? 0} permissionsRequired={[[PermissionDomain.Registration, PermissionAction.Write]]}>
          <UiHStack
            px={0}
            justifyContent={'flex-start'}
            onClick={() => { return toOrderInfoPage(row.original.id); }}
            {...uiStyles.hover}
          >
            <UiIconArrowRight size={'2xl'} color={'primary.500'} />
          </UiHStack>
        </BaseAclContainer>
      );
    },
    ...dataTableOptions,
  });

  return <MantineReactTable table={table} />;
};

export default Datatable;
